import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, Users, Calendar, Clock, MessageSquare, Package, Shield, TrendingUp } from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();

  const features = [
    { icon: <Users className="w-6 h-6" />, title: 'Employee Management', description: 'Manage your workforce with ease' },
    { icon: <Calendar className="w-6 h-6" />, title: 'Smart Scheduling', description: 'Drag-and-drop shift planning' },
    { icon: <Clock className="w-6 h-6" />, title: 'Time Tracking', description: 'Accurate clock-in/clock-out system' },
    { icon: <MessageSquare className="w-6 h-6" />, title: 'Team Communication', description: 'Keep everyone connected' },
    { icon: <Package className="w-6 h-6" />, title: 'Material Ordering', description: 'HVAC & Plumbing supplies management' },
    { icon: <Shield className="w-6 h-6" />, title: 'Multi-role Access', description: 'Admin, Purchaser, Employee roles' },
  ];

  const pricingPlans = [
    {
      name: 'Starter',
      price: '55',
      employees: 'Up to 8 people',
      features: ['Up to 8 employees', 'Basic scheduling', 'Time tracking', 'Email support']
    },
    {
      name: 'Small Business',
      price: '125',
      employees: 'Up to 20 people',
      features: ['Up to 20 employees', 'Advanced scheduling', 'Time tracking with GPS', 'Priority support', 'Project management', 'Project-specific communication', 'Upload pictures for projects']
    },
    {
      name: 'Mid Size Business',
      price: '250',
      employees: 'Up to 45 people',
      features: ['Up to 45 employees', 'All Small Business features', 'Material ordering', 'Stock management', 'Expense reimbursement', '📱 Mobile App (PWA)', '🔔 Push Notifications']
    },
    {
      name: 'Big Business',
      price: '350',
      employees: '46-75 people',
      features: ['46-75 employees', 'All Mid Size features', 'Create and manage sub-projects', 'Manage material returns', 'Manage material transfers', 'Upload videos', 'Add subcontractors', 'Upload invoices', 'Equipment management', 'Bi-weekly payroll summary', 'Excel export']
    },
    {
      name: 'Enterprise',
      price: 'Contact',
      employees: 'More than 76',
      features: ['Unlimited employees (76+)', 'All Big Business features', 'White-label option', '24/7 phone support', 'Custom development', 'SLA guarantee'],
      isEnterprise: true
    }
  ];

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="header" data-testid="landing-header">
        <div className="container">
          <div className="logo" data-testid="logo">WorkForce Pro</div>
          <nav>
            <Button variant="ghost" onClick={() => navigate('/auth')} data-testid="login-btn">Login</Button>
            <Button onClick={() => navigate('/auth')} data-testid="get-started-btn">Get Started</Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero" data-testid="hero-section">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title" data-testid="hero-title">
              Streamline Your HVAC & Plumbing Workforce
            </h1>
            <p className="hero-subtitle" data-testid="hero-subtitle">
              Complete workforce management solution for construction teams. Manage schedules, track time, order materials, and communicate seamlessly.
            </p>
            <div className="hero-buttons">
              <Button size="lg" onClick={() => navigate('/auth')} data-testid="hero-cta-btn">
                Start Free Trial
              </Button>
              <Button size="lg" variant="outline" data-testid="watch-demo-btn">
                Watch Demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features" data-testid="features-section">
        <div className="container">
          <h2 className="section-title">Everything You Need to Manage Your Team</h2>
          <div className="features-grid">
            {features.map((feature, index) => (
              <Card key={index} className="feature-card" data-testid={`feature-card-${index}`}>
                <CardHeader>
                  <div className="feature-icon">{feature.icon}</div>
                  <CardTitle>{feature.title}</CardTitle>
                  <CardDescription>{feature.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing" data-testid="pricing-section">
        <div className="container">
          <h2 className="section-title">Simple, Transparent Pricing</h2>
          <p className="section-subtitle">Choose the plan that fits your business</p>
          <div className="pricing-grid">
            {pricingPlans.map((plan, index) => (
              <Card key={index} className={`pricing-card ${index === 1 ? 'popular' : ''}`} data-testid={`pricing-card-${index}`}>
                {index === 1 && <div className="popular-badge">Most Popular</div>}
                <CardHeader>
                  <CardTitle>{plan.name}</CardTitle>
                  <div className="price">
                    {plan.isEnterprise ? (
                      <span className="price-amount">Contact Us</span>
                    ) : (
                      <>
                        <span className="price-amount">${plan.price}</span>
                        <span className="price-period">/month</span>
                      </>
                    )}
                  </div>
                  <CardDescription>{plan.employees}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="features-list">
                    {plan.features.map((feature, i) => (
                      <li key={i}>
                        <CheckCircle2 className="w-5 h-5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className="w-full" 
                    variant={index === 1 ? 'default' : 'outline'} 
                    onClick={() => navigate('/auth')} 
                    data-testid={`select-plan-${index}-btn`}
                  >
                    {plan.isEnterprise ? 'Contact Sales' : 'Get Started'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta" data-testid="cta-section">
        <div className="container">
          <h2>Ready to Transform Your Workforce Management?</h2>
          <p>Join hundreds of HVAC and plumbing companies already using WorkForce Pro</p>
          <Button size="lg" onClick={() => navigate('/auth')} data-testid="cta-footer-btn">
            Start Your Free Trial
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="container">
          <p>&copy; 2025 WorkForce Pro. All rights reserved.</p>
        </div>
      </footer>

      <style jsx>{`
        .landing-page {
          background: #f8fafc;
        }

        .header {
          position: sticky;
          top: 0;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid #e2e8f0;
          padding: 1rem 0;
          z-index: 100;
        }

        .header .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .logo {
          font-family: 'Space Grotesk', sans-serif;
          font-size: 1.5rem;
          font-weight: 700;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .header nav {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .hero {
          padding: 6rem 0;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          position: relative;
          overflow: hidden;
        }

        .hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
        }

        .hero-content {
          max-width: 800px;
          margin: 0 auto;
          text-align: center;
          position: relative;
          z-index: 1;
        }

        .hero-title {
          font-size: clamp(2.5rem, 5vw, 4rem);
          font-weight: 700;
          margin-bottom: 1.5rem;
          line-height: 1.1;
        }

        .hero-subtitle {
          font-size: 1.25rem;
          margin-bottom: 2rem;
          opacity: 0.95;
          line-height: 1.6;
        }

        .hero-buttons {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        .section-title {
          font-size: clamp(2rem, 4vw, 2.5rem);
          font-weight: 700;
          text-align: center;
          margin-bottom: 1rem;
          color: #1e293b;
        }

        .section-subtitle {
          text-align: center;
          color: #64748b;
          font-size: 1.125rem;
          margin-bottom: 3rem;
        }

        .features {
          padding: 5rem 0;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
        }

        .feature-card {
          border: 1px solid #e2e8f0;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .feature-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
        }

        .feature-icon {
          width: 3rem;
          height: 3rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin-bottom: 1rem;
        }

        .pricing {
          padding: 5rem 0;
          background: white;
        }

        .pricing-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
          margin-top: 3rem;
          max-width: 1400px;
          margin-left: auto;
          margin-right: auto;
        }

        .pricing-card {
          border: 2px solid #e2e8f0;
          position: relative;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .pricing-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
        }

        .pricing-card.popular {
          border-color: #667eea;
          box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
        }

        .popular-badge {
          position: absolute;
          top: -12px;
          left: 50%;
          transform: translateX(-50%);
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 0.25rem 1rem;
          border-radius: 20px;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .price {
          margin: 1.5rem 0;
        }

        .price-amount {
          font-size: 3rem;
          font-weight: 700;
          color: #1e293b;
        }

        .price-period {
          color: #64748b;
          font-size: 1rem;
        }

        .features-list {
          list-style: none;
          padding: 0;
          margin: 1.5rem 0;
        }

        .features-list li {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.75rem;
          color: #475569;
        }

        .features-list li svg {
          color: #667eea;
          flex-shrink: 0;
        }

        .cta {
          padding: 5rem 0;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          text-align: center;
        }

        .cta h2 {
          font-size: clamp(2rem, 4vw, 2.5rem);
          margin-bottom: 1rem;
        }

        .cta p {
          font-size: 1.25rem;
          margin-bottom: 2rem;
          opacity: 0.95;
        }

        .footer {
          padding: 2rem 0;
          background: #1e293b;
          color: white;
          text-align: center;
        }

        /* ====================================
           MOBILE RESPONSIVE STYLES
           ==================================== */
        
        @media (max-width: 768px) {
          /* Navigation */
          nav {
            padding: 1rem !important;
          }

          .nav-links {
            display: none; /* Consider adding hamburger menu */
          }

          /* Hero Section */
          .hero {
            padding: 3rem 1rem;
          }

          .hero h1 {
            font-size: 2rem !important;
            line-height: 1.2 !important;
          }

          .hero p {
            font-size: 1rem !important;
          }

          .hero-buttons {
            flex-direction: column;
            gap: 1rem;
            width: 100%;
          }

          .hero-buttons button {
            width: 100%;
            min-height: 48px;
            font-size: 1rem;
          }

          /* Features Section */
          .features {
            padding: 2.5rem 1rem;
          }

          .features h2 {
            font-size: 1.75rem !important;
          }

          .features-grid {
            grid-template-columns: 1fr !important;
            gap: 1.5rem;
          }

          .feature-card {
            padding: 1.5rem !important;
          }

          .feature-card h3 {
            font-size: 1.25rem !important;
          }

          .feature-card p {
            font-size: 0.9375rem !important;
          }

          /* Pricing Section */
          .pricing {
            padding: 2.5rem 1rem;
          }

          .pricing h2 {
            font-size: 1.75rem !important;
          }

          .pricing-grid {
            grid-template-columns: 1fr !important;
            gap: 1.5rem;
            max-width: 400px;
          }

          .pricing-card {
            padding: 1.5rem !important;
          }

          .price-amount {
            font-size: 2.5rem !important;
          }

          .price-period {
            font-size: 1rem !important;
          }

          .popular-badge {
            font-size: 0.75rem !important;
            padding: 0.25rem 0.75rem !important;
          }

          .features-list {
            gap: 0.5rem !important;
          }

          .features-list li {
            font-size: 0.875rem !important;
          }

          /* CTA Section */
          .cta {
            padding: 2.5rem 1rem;
          }

          .cta h2 {
            font-size: 1.75rem !important;
          }

          .cta p {
            font-size: 1rem !important;
          }

          .cta button {
            width: 100%;
            max-width: 300px;
            min-height: 48px;
            font-size: 1rem;
          }

          /* Footer */
          footer {
            padding: 1.5rem 1rem !important;
            font-size: 0.875rem !important;
          }

          /* Buttons */
          button {
            min-height: 44px !important;
            padding: 0.75rem 1.5rem !important;
          }
        }

        @media (max-width: 375px) {
          .hero h1 {
            font-size: 1.75rem !important;
          }

          .hero p {
            font-size: 0.9375rem !important;
          }

          .features h2,
          .pricing h2,
          .cta h2 {
            font-size: 1.5rem !important;
          }

          .price-amount {
            font-size: 2rem !important;
          }

          .pricing-grid {
            max-width: 100%;
          }
        }

        /* Landscape Mode */
        @media (max-height: 500px) and (orientation: landscape) {
          .hero {
            padding: 2rem 1rem;
          }

          .hero h1 {
            font-size: 1.5rem !important;
          }

          .hero p {
            font-size: 0.875rem !important;
          }
        }
      `}</style>
    </div>
  );
}
